import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEMO_SCENARIOS,
  deriveActivity,
  deriveLeadSnapshot,
  deriveSummaryMetrics,
  deriveTransferStatus,
  findDemoScenario,
  formatDemoClock,
  getScenarioDuration,
  getScenarioEvents,
  getVisibleEvents,
  type DemoActivity,
  type DemoLeadSnapshot,
  type DemoScenario,
  type DemoScenarioId,
  type DemoSpeed,
  type DemoSummaryMetrics,
  type DemoTransferStatus,
} from './demoModel.js';

export interface UseDemoEngineOptions {
  initialScenarioId?: DemoScenarioId;
  autoPlay?: boolean;
  initialSpeed?: DemoSpeed;
  initialTransferAvailable?: boolean;
  tickMs?: number;
}

export interface DemoEngine {
  scenarios: readonly DemoScenario[];
  scenario: DemoScenario;
  selectedScenarioId: DemoScenarioId;
  selectScenario: (scenarioId: DemoScenarioId) => void;
  leads: readonly DemoLeadSnapshot[];
  lead: DemoLeadSnapshot;
  selectedLeadId: string;
  selectLead: (leadId: string) => void;
  viewLead: (leadId: string) => void;
  events: ReturnType<typeof getVisibleEvents>;
  activity: readonly DemoActivity[];
  summary: DemoSummaryMetrics;
  transfer: DemoTransferStatus;
  transferAvailable: boolean;
  setTransferAvailable: (available: boolean) => void;
  isPlaying: boolean;
  isComplete: boolean;
  play: () => void;
  pause: () => void;
  togglePlayback: () => void;
  restart: () => void;
  speed: DemoSpeed;
  setSpeed: (speed: DemoSpeed) => void;
  clockMs: number;
  durationMs: number;
  progress: number;
  clockLabel: string;
}

export function useDemoEngine(options: UseDemoEngineOptions = {}): DemoEngine {
  const {
    initialScenarioId = DEMO_SCENARIOS[0].id,
    autoPlay = true,
    initialSpeed = 1,
    initialTransferAvailable = true,
    tickMs = 50,
  } = options;

  const [selectedScenarioId, setSelectedScenarioId] =
    useState<DemoScenarioId>(initialScenarioId);
  const [clockMs, setClockMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [speed, setSpeedState] = useState<DemoSpeed>(initialSpeed);
  const [transferAvailable, setTransferAvailableState] = useState(initialTransferAvailable);

  const scenario = useMemo(() => findDemoScenario(selectedScenarioId), [selectedScenarioId]);
  const durationMs = getScenarioDuration(scenario, transferAvailable);
  const isComplete = clockMs >= durationMs;

  useEffect(() => {
    if (!isPlaying || isComplete) {
      return;
    }

    let previousTick = performance.now();
    const intervalId = window.setInterval(() => {
      const currentTick = performance.now();
      const elapsed = currentTick - previousTick;
      previousTick = currentTick;
      setClockMs((current) => Math.min(durationMs, current + elapsed * speed));
    }, tickMs);

    return () => window.clearInterval(intervalId);
  }, [durationMs, isComplete, isPlaying, speed, tickMs]);

  useEffect(() => {
    if (isComplete && isPlaying) {
      setIsPlaying(false);
    }
  }, [isComplete, isPlaying]);

  const restart = useCallback(() => {
    setClockMs(0);
    setIsPlaying(true);
  }, []);

  const selectScenario = useCallback((scenarioId: DemoScenarioId) => {
    setSelectedScenarioId(scenarioId);
    setClockMs(0);
    setIsPlaying(true);
  }, []);

  const selectLead = useCallback((leadId: string) => {
    const leadScenario = DEMO_SCENARIOS.find((candidate) => candidate.lead.id === leadId);
    if (!leadScenario) {
      return;
    }

    setSelectedScenarioId(leadScenario.id);
    setClockMs(0);
    setIsPlaying(true);
  }, []);

  const viewLead = useCallback((leadId: string) => {
    const leadScenario = DEMO_SCENARIOS.find((candidate) => candidate.lead.id === leadId);
    if (!leadScenario) {
      return;
    }

    setSelectedScenarioId(leadScenario.id);
    setClockMs(getScenarioDuration(leadScenario, transferAvailable));
    setIsPlaying(false);
  }, [transferAvailable]);

  const setTransferAvailable = useCallback((available: boolean) => {
    setTransferAvailableState(available);
    setClockMs(0);
    setIsPlaying(true);
  }, []);

  const play = useCallback(() => {
    if (clockMs >= durationMs) {
      setClockMs(0);
    }
    setIsPlaying(true);
  }, [clockMs, durationMs]);

  const pause = useCallback(() => setIsPlaying(false), []);
  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (clockMs >= durationMs) {
      setClockMs(0);
    }
    setIsPlaying(true);
  }, [clockMs, durationMs, isPlaying]);

  const setSpeed = useCallback((nextSpeed: DemoSpeed) => {
    setSpeedState(nextSpeed);
  }, []);

  const events = useMemo(
    () => getVisibleEvents(scenario, clockMs, transferAvailable),
    [clockMs, scenario, transferAvailable],
  );
  const lead = useMemo(() => deriveLeadSnapshot(scenario, events), [events, scenario]);
  const activity = useMemo(() => deriveActivity(events), [events]);
  const summary = useMemo(() => deriveSummaryMetrics(events, lead), [events, lead]);
  const transfer = useMemo(
    () => deriveTransferStatus(events, transferAvailable),
    [events, transferAvailable],
  );

  const leads = useMemo(
    () =>
      DEMO_SCENARIOS.map((candidate) => {
        if (candidate.id === scenario.id) {
          return lead;
        }

        const candidateEvents = getScenarioEvents(candidate, transferAvailable);
        return deriveLeadSnapshot(candidate, candidateEvents);
      }),
    [lead, scenario.id, transferAvailable],
  );

  return {
    scenarios: DEMO_SCENARIOS,
    scenario,
    selectedScenarioId,
    selectScenario,
    leads,
    lead,
    selectedLeadId: lead.id,
    selectLead,
    viewLead,
    events,
    activity,
    summary,
    transfer,
    transferAvailable,
    setTransferAvailable,
    isPlaying,
    isComplete,
    play,
    pause,
    togglePlayback,
    restart,
    speed,
    setSpeed,
    clockMs,
    durationMs,
    progress: durationMs > 0 ? Math.min(1, clockMs / durationMs) : 0,
    clockLabel: `${formatDemoClock(clockMs)} / ${formatDemoClock(durationMs)}`,
  };
}
